import * as React from "react";
import { styled } from "styled-components";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { RichText } from "../../internal-components/RichText.js";
import { Chip } from "../../internal-components/Chip.js";
import {
  starEmpty,
  starFilled,
  starHalf,
} from "../../icons/index.js";
import { createEditableProps } from "@ludens-reklame/rubics-v3-react/util/fns";

export interface Review {
  title?: string;
  text?: string;
  rating?:
    | "0"
    | "0.5"
    | "1"
    | "1.5"
    | "2"
    | "2.5"
    | "3"
    | "3.5"
    | "4"
    | "4.5"
    | "5";
  tags?: { tag?: string }[];
}

export interface Props {
  reviews?: Review[];
}

export const Reviews: React.FunctionComponent<Props> = ({ reviews = [] }) => {
  const [filteredTags, setFilteredTags] = React.useState<string[]>([]);

  const searchableTags = React.useMemo<string[]>(() => {
    const tags: string[] = [];

    for (let i = 0; i < reviews.length; i++) {
      const review = reviews[i];
      const reviewTags = review.tags || [];

      for (let ii = 0; ii < reviewTags.length; ii++) {
        const tag = reviewTags[ii];

        if (tag.tag && !tags.includes(tag.tag)) {
          tags.push(tag.tag);
        }
      }
    }

    return tags;
  }, [reviews]);

  const toggleTag = React.useCallback(
    (tag: string) => {
      if (filteredTags.includes(tag)) {
        setFilteredTags(filteredTags.filter((t) => t !== tag));
      } else {
        setFilteredTags([...filteredTags, tag]);
      }
    },
    [filteredTags]
  );

  const filteredReviews = React.useMemo<Review[]>(() => {
    if (filteredTags.length === 0) {
      return reviews;
    }

    return reviews.filter((r) => {
      return r.tags?.some((t) => filteredTags.includes(t.tag || ""));
    });
  }, [reviews, filteredTags]);

  if (reviews.length === 0) {
    return null;
  }

  return (
    <VerticalSpacer>
      <HorizontalSpacer>
        <Constrainer>
          {searchableTags.length > 0 && (
            <>
              <p>Filtrer på anmeldelser innen:</p>
              <Tags>
                {searchableTags.map((t) => (
                  <li key={t}>
                    <Chip
                      $active={filteredTags.includes(t)}
                      onClick={() => toggleTag(t)}
                    >
                      {t}
                    </Chip>
                  </li>
                ))}
              </Tags>
            </>
          )}
          <Grid>
            {filteredReviews.map((r, k) => {
              const ratedStars = parseInt(r.rating || "0");
              const halfStar = r.rating?.includes(".5");
              const restStars = 5 - ratedStars - (halfStar ? 1 : 0);
              const stars: React.ReactNode[] = [];

              for (let i = 0; i < ratedStars; i++) {
                stars.push(starFilled);
              }

              if (halfStar) {
                stars.push(starHalf);
              }

              for (let i = 0; i < restStars; i++) {
                stars.push(starEmpty);
              }

              return (
                <Item key={k}>
                  {r.title && (
                    <h2
                      className="b3"
                      {...createEditableProps("text", `reviews.${k}.title`)}
                    >
                      {r.title}
                    </h2>
                  )}
                  {r.text && (
                    <RichText
                      dangerouslySetInnerHTML={{ __html: r.text }}
                      {...createEditableProps("html", `reviews.${k}.text`)}
                    />
                  )}
                  <Rating>
                    {stars.map((s, k) => (
                      <i key={k} aria-hidden="true">
                        {s}
                      </i>
                    ))}
                    <p className="hidden">{r.rating || "0"} av 5 stjerner</p>
                  </Rating>
                </Item>
              );
            })}
          </Grid>
        </Constrainer>
      </HorizontalSpacer>
    </VerticalSpacer>
  );
};

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: ${(props) => props.theme.spacing.l};

  @media (min-width: ${mobileBreakPointCSS}) {
    flex-direction: row;
    flex-wrap: wrap;

    > * {
      width: calc(50% - ${(props) => props.theme.spacing.l} / 2);
    }

    > *:nth-child(4n - 1) {
      width: 400px;
    }

    > *:nth-child(4n) {
      width: auto;
      flex: 1;
    }
  }
`;

const Item = styled.div`
  background: var(--swatch3);
  color: var(--swatch2);
  padding: ${(props) => props.theme.spacing.l};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  border-radius: ${(props) => props.theme.borderRadius};

  > *:first-child {
    margin-top: 0;
  }

  > div > *:first-child {
    margin-top: 0;
  }

  > div > *:last-child {
    margin-bottom: 0;
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: ${(props) => props.theme.spacing.xl};
  }
`;

const Tags = styled.ul`
  display: flex;
  margin: ${(props) =>
    `${props.theme.spacing.m} -${props.theme.spacing.m} ${props.theme.spacing.l}`};
  padding: ${(props) => `0 0 0 ${props.theme.spacing.m}`};
  gap: ${(props) => props.theme.spacing.s};
  list-style: none;
  overflow-x: auto;
`;

const Rating = styled.div`
  margin-top: auto;
  padding-top: ${(props) => props.theme.spacing.l};
  display: flex;
  gap: 7px;

  path {
    fill: var(--primary-icon-color);
  }
`;
